/** What a weekly room's sessions do, for the computer's Sessions tab and Season's: the lists, a session's menu and its dialogs. */
import { useState } from "react";
import type { LiveChangeResponse, LiveSessionView, RoomAudienceResponse, WebinarDetail, WebinarLive } from "../../../shared/types";
import { SESSION_TOPIC_MAX } from "../../../shared/live-series";
import { aheadOf, timeOf, toldLine, weekly } from "../../board/series";
import { ChangeSeries, EndSeries, MoveSession, SkipSession } from "../../board/SeriesDialogs";
import { Button } from "../../components/ui/Button";
import { Field } from "../../components/ui/Field";
import type { MenuItem } from "../../components/ui/Menu";
import { Sheet } from "../../components/ui/Sheet";
import { useToast } from "../../components/ui/Toast";
import { ApiError, api } from "../../lib/api";
import { type PrivateSend, PrivateSendSheet, startMessage } from "./PrivateSendSheet";
import { longDay } from "./liveWords";
import { GROUP_WORDS } from "./roomWords";

export const PAST_SHOWN = 4;

type Pop = { kind: "move" | "skip" | "topic"; session: LiveSessionView } | { kind: "change" | "end" };

/** Set or change one session's topic. */
function TopicSheet({
  webinarId,
  session,
  onSaved,
  onClose,
}: {
  webinarId: string;
  session: LiveSessionView;
  onSaved: (res: LiveChangeResponse, said: string) => void;
  onClose: () => void;
}) {
  const [topic, setTopic] = useState(session.topic ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const changed = topic.trim() !== (session.topic ?? "");
  const save = () => {
    if (busy || !changed) return;
    setBusy(true);
    setError("");
    api.board
      .changeSession(webinarId, session.id, { action: "topic", topic: topic.trim() })
      .then((res) => {
        onSaved(res, topic.trim() ? "Topic saved" : "Topic cleared");
        onClose();
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : "That didn't save. Try again."))
      .finally(() => setBusy(false));
  };
  return (
    <Sheet
      open
      onClose={onClose}
      title={session.topic ? "Change its topic" : "Set its topic"}
      sub={`${longDay(session.startsAt, session.timezone)}, ${timeOf(session.startsAt, session.timezone)}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" disabled={busy || !changed} onClick={save}>
            {busy ? "Saving…" : "Save the topic"}
          </Button>
        </>
      }
    >
      <Field label="What will this one cover?" htmlFor="tp" error={error || undefined}>
        <input dir="auto"
          id="tp"
          className="inp"
          value={topic}
          maxLength={SESSION_TOPIC_MAX}
          placeholder="Staging on a budget"
          onChange={(e) => setTopic(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
          }}
        />
      </Field>
    </Sheet>
  );
}


/** The sessions of a series as the page reads them, with what each may do and the dialogs that do it. */
export function useSessions(detail: WebinarDetail, live: WebinarLive | undefined, onSaved: (res: LiveChangeResponse, said: string) => void) {
  const toast = useToast();
  const [pop, setPop] = useState<Pop | null>(null);
  const [sending, setSending] = useState<PrivateSend | null>(null);
  const series = live?.series;
  const tz = series?.rule.timezone ?? live?.timezone ?? "UTC";
  const now = Date.now();
  const sorted = (series?.sessions ?? []).toSorted((a, b) => a.seq - b.seq);
  const upcoming = sorted.filter((s) => s.state === "live" || aheadOf(s, now));
  const past = sorted.filter((s) => !upcoming.includes(s)).toReversed();
  const ran = past.filter((s) => s.state === "ended").length;
  const first = upcoming.find((s) => s.state !== "skipped");
  const firstRan = sorted.find((s) => s.state === "ended");
  const told = toldLine(series?.seats ?? 0);
  const lengthMin = sorted.at(-1)?.lengthMin ?? live?.lengthMin ?? 0;

  const direct = (s: LiveSessionView, body: { action: "back" } | { action: "restore" }, said: string) =>
    api.board
      .changeSession(detail.id, s.id, body)
      .then((res) => onSaved(res, said))
      .catch((e: unknown) => toast(e instanceof ApiError ? e.message : "That didn't save. Try again."));

  /** What one coming session offers: a topic, move, skip, bring back, move back. */
  const menuFor = (s: LiveSessionView): MenuItem[] => {
    if (s.state === "skipped") {
      return [{ label: "Bring this one back", icon: "undo", onSelect: () => void direct(s, { action: "restore" }, `It's back on.${told}`) }];
    }
    const items: MenuItem[] = [];
    if (s.state === "scheduled") {
      items.push({ label: "Move this one", icon: "cal", onSelect: () => setPop({ kind: "move", session: s }) });
      items.push({ label: "Skip this one", icon: "x", onSelect: () => setPop({ kind: "skip", session: s }) });
      if (s.movedFrom) {
        const home = s.movedFrom;
        items.push({
          label: `Move it back to ${longDay(home, tz)}`,
          icon: "undo",
          onSelect: () => void direct(s, { action: "back" }, `Back on ${longDay(home, tz)} at ${timeOf(home, tz)}.${told}`),
        });
      }
    }
    return items;
  };

  const sendDropped = (dropped: { sent: unknown; reach: number; faces: PrivateSend["faces"] }, hostFirst: string) =>
    setSending({
      seed: { webinarId: detail.id, scope: "series", key: "dropped" },
      title: dropped.sent ? GROUP_WORDS.dropped.again(dropped.reach) : `Send the ${dropped.reach.toLocaleString("en-US")} who dropped off a note`,
      reach: dropped.reach,
      faces: dropped.faces,
      label: `Dropped off, ${detail.title}`,
      subject: GROUP_WORDS.dropped.subject,
      message: startMessage(GROUP_WORDS.dropped.message, hostFirst),
    });

  return { series, tz, now, upcoming, past, ran, first, firstRan, told, lengthMin, menuFor, pop, setPop, sending, setSending, sendDropped };
}

/** The dialogs a series opens: a topic, move, skip, change the time, end the series, and the note to the ones who dropped off. */
export function SessionDialogs({
  detail,
  live,
  state,
  onSaved,
  onSent,
}: {
  detail: WebinarDetail;
  live: WebinarLive | undefined;
  state: ReturnType<typeof useSessions>;
  onSaved: (res: LiveChangeResponse, said: string) => void;
  /** A note went out: the series' groups are read again, so they say what was sent. */
  onSent: () => void;
}) {
  const { series, upcoming, pop, setPop, sending, setSending } = state;
  if (!series || !live) return null;
  return (
    <>
      {pop?.kind === "topic" ? <TopicSheet webinarId={detail.id} session={pop.session} onSaved={onSaved} onClose={() => setPop(null)} /> : null}
      {pop?.kind === "move" ? (
        <MoveSession webinarId={detail.id} session={pop.session} rule={series.rule} seats={series.seats} onSaved={onSaved} onClose={() => setPop(null)} />
      ) : null}
      {pop?.kind === "skip" ? (
        <SkipSession
          webinarId={detail.id}
          session={pop.session}
          next={upcoming.find((x) => x.seq > pop.session.seq && x.state === "scheduled") ?? null}
          byWeek={weekly(series.rule)}
          seats={series.seats}
          onSaved={onSaved}
          onClose={() => setPop(null)}
        />
      ) : null}
      {pop?.kind === "change" ? <ChangeSeries webinarId={detail.id} live={live} onSaved={onSaved} onClose={() => setPop(null)} /> : null}
      {pop?.kind === "end" ? (
        <EndSeries
          webinarId={detail.id}
          toCome={series.rule.ends.kind === "never" ? null : upcoming.filter((s) => s.state === "scheduled").length}
          seats={series.seats}
          onSaved={onSaved}
          onClose={() => setPop(null)}
        />
      ) : null}
      {sending ? <PrivateSendSheet key="dropped" send={sending} onClose={() => setSending(null)} onSent={onSent} /> : null}
    </>
  );
}

/** The series' groups across its sessions: the group by key, from the read the page keeps. */
export function acrossGroup(across: RoomAudienceResponse | null, key: "regulars" | "dropped" | "joined_late") {
  return across?.groups.find((g) => g.key === key) ?? null;
}
